import { useEffect, useRef, useState } from 'react';

interface UseLoadingTimeoutProps {
  resource?: string;
  hasLoaded: boolean;
  timeoutEnabled?: boolean;
  timeout?: number;
  onTimeout?: (error: string) => void;
}

interface UseLoadingTimeoutReturn {
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

export const useLoadingTimeout = ({
  resource,
  hasLoaded,
  timeoutEnabled = true,
  timeout = 5000,
  onTimeout,
}: UseLoadingTimeoutProps): UseLoadingTimeoutReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!resource) {
      setIsLoading(false);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!hasLoaded) {
      setIsLoading(true);
      setError(null);

      timeoutRef.current = setTimeout(() => {
        if (!hasLoaded && timeoutEnabled) {
          const timeoutError = 'Loading timed out';
          setError(timeoutError);
          onTimeout?.(timeoutError);
          setIsLoading(false);
        }
      }, timeout);
    } else {
      setIsLoading(false);
      setError(null);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [resource, hasLoaded, timeoutEnabled, timeout, onTimeout]);

  return { isLoading, error, setError };
};
